import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogFormGrid, DialogFormSection,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { eduCalendarAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { CalendarDays, Plus, Pencil, Trash2, Loader2, CalendarRange, CalendarCheck, Ban } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const TERM_TYPES = ['semester', 'trimester', 'term'];
const TERM_TYPE_LABEL = { semester: 'Semester', trimester: 'Trimester', term: 'Term' };

const EVENT_TYPES = ['holiday', 'exam', 'admission', 'orientation', 'convocation', 'assessment', 'result'];
const EVENT_TYPE_LABEL = {
  holiday: 'Holiday', exam: 'Exam', admission: 'Admission', orientation: 'Orientation',
  convocation: 'Convocation', assessment: 'Assessment', result: 'Result',
};

const EMPTY_YEAR = { name: '', starts_on: '', ends_on: '', is_current: false };
const EMPTY_TERM = { academic_year_id: '', name: '', term_type: 'semester', starts_on: '', ends_on: '' };
const EMPTY_EVENT = { term_id: '', event_type: 'holiday', title: '', starts_on: '', ends_on: '', blocks_teaching: false };

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

const fmtDate = (v) => (v ? String(v).slice(0, 10) : '—');
const cleanDates = (obj, keys) => {
  const out = { ...obj };
  keys.forEach((k) => { if (out[k] === '' || out[k] == null) out[k] = null; });
  return out;
};

const EducationCalendarManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [tab, setTab] = useState('years'); // years | terms | events
  const [loading, setLoading] = useState(false);
  const [years, setYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [events, setEvents] = useState([]);

  const [yearDialog, setYearDialog] = useState(false);
  const [termDialog, setTermDialog] = useState(false);
  const [eventDialog, setEventDialog] = useState(false);

  const [yearForm, setYearForm] = useState(EMPTY_YEAR);
  const [termForm, setTermForm] = useState(EMPTY_TERM);
  const [eventForm, setEventForm] = useState(EMPTY_EVENT);
  const [editingYear, setEditingYear] = useState(null);
  const [editingTerm, setEditingTerm] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [y, t, e] = await Promise.all([
        eduCalendarAPI.listYears(),
        eduCalendarAPI.listTerms(),
        eduCalendarAPI.listEvents(),
      ]);
      setYears(toArray(y));
      setTerms(toArray(t));
      setEvents(toArray(e));
    } catch (err) {
      if (err?.response?.status === 402) {
        setYears([]); setTerms([]); setEvents([]);
      } else {
        Swal.fire('Could not load calendar', err?.response?.data?.detail || 'Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const yearName = (id) => years.find((y) => y.id === id)?.name || `#${id}`;
  const termName = (id) => (id ? (terms.find((t) => t.id === id)?.name || `#${id}`) : '—');

  const stats = useMemo(() => {
    const current = years.find((y) => y.is_current);
    const blocking = events.filter((e) => e.blocks_teaching).length;
    return { years: years.length, terms: terms.length, events: events.length, blocking, current: current?.name };
  }, [years, terms, events]);

  // ---------------- Years ----------------
  const openYearCreate = () => { setEditingYear(null); setYearForm(EMPTY_YEAR); setYearDialog(true); };
  const openYearEdit = (row) => {
    setEditingYear(row.id);
    setYearForm({
      name: row.name || '', starts_on: fmtDate(row.starts_on) === '—' ? '' : fmtDate(row.starts_on),
      ends_on: fmtDate(row.ends_on) === '—' ? '' : fmtDate(row.ends_on), is_current: Boolean(row.is_current),
    });
    setYearDialog(true);
  };
  const submitYear = async () => {
    if (!yearForm.name?.trim()) return Swal.fire('Name required', 'Enter an academic year name (e.g. 2025-2026).', 'warning');
    const payload = cleanDates(yearForm, ['starts_on', 'ends_on']);
    try {
      if (editingYear) await eduCalendarAPI.updateYear(editingYear, payload);
      else await eduCalendarAPI.createYear(payload);
      setYearDialog(false);
      await loadAll();
      Swal.fire({ icon: 'success', title: editingYear ? 'Year updated' : 'Year added', timer: 1300, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please review and try again.', 'error');
    }
  };
  const removeYear = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Delete academic year?',
      text: `${row.name} and its terms will be removed. Events are detached, not deleted.`,
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try { await eduCalendarAPI.deleteYear(row.id); await loadAll(); Swal.fire({ icon: 'success', title: 'Deleted', timer: 1100, showConfirmButton: false }); }
    catch (err) { Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error'); }
  };

  // ---------------- Terms ----------------
  const openTermCreate = () => { setEditingTerm(null); setTermForm({ ...EMPTY_TERM, academic_year_id: years[0] ? String(years[0].id) : '' }); setTermDialog(true); };
  const openTermEdit = (row) => {
    setEditingTerm(row.id);
    setTermForm({
      academic_year_id: String(row.academic_year_id), name: row.name || '', term_type: row.term_type || 'semester',
      starts_on: fmtDate(row.starts_on) === '—' ? '' : fmtDate(row.starts_on),
      ends_on: fmtDate(row.ends_on) === '—' ? '' : fmtDate(row.ends_on),
    });
    setTermDialog(true);
  };
  const submitTerm = async () => {
    if (!termForm.academic_year_id) return Swal.fire('Year required', 'Select the academic year this term belongs to.', 'warning');
    if (!termForm.name?.trim()) return Swal.fire('Name required', 'Enter a term name (e.g. Autumn Semester).', 'warning');
    const payload = cleanDates({ ...termForm, academic_year_id: Number(termForm.academic_year_id) }, ['starts_on', 'ends_on']);
    try {
      if (editingTerm) await eduCalendarAPI.updateTerm(editingTerm, payload);
      else await eduCalendarAPI.createTerm(payload);
      setTermDialog(false);
      await loadAll();
      Swal.fire({ icon: 'success', title: editingTerm ? 'Term updated' : 'Term added', timer: 1300, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please review and try again.', 'error');
    }
  };
  const removeTerm = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Delete term?', text: `${row.name} will be removed. Its events are detached, not deleted.`,
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try { await eduCalendarAPI.deleteTerm(row.id); await loadAll(); Swal.fire({ icon: 'success', title: 'Deleted', timer: 1100, showConfirmButton: false }); }
    catch (err) { Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error'); }
  };

  // ---------------- Events ----------------
  const openEventCreate = () => { setEditingEvent(null); setEventForm(EMPTY_EVENT); setEventDialog(true); };
  const openEventEdit = (row) => {
    setEditingEvent(row.id);
    setEventForm({
      term_id: row.term_id ? String(row.term_id) : '', event_type: row.event_type || 'holiday', title: row.title || '',
      starts_on: fmtDate(row.starts_on) === '—' ? '' : fmtDate(row.starts_on),
      ends_on: fmtDate(row.ends_on) === '—' ? '' : fmtDate(row.ends_on),
      blocks_teaching: Boolean(row.blocks_teaching),
    });
    setEventDialog(true);
  };
  const submitEvent = async () => {
    if (!eventForm.title?.trim()) return Swal.fire('Title required', 'Enter an event title.', 'warning');
    const payload = cleanDates(
      { ...eventForm, term_id: eventForm.term_id ? Number(eventForm.term_id) : null },
      ['starts_on', 'ends_on'],
    );
    try {
      if (editingEvent) await eduCalendarAPI.updateEvent(editingEvent, payload);
      else await eduCalendarAPI.createEvent(payload);
      setEventDialog(false);
      await loadAll();
      Swal.fire({ icon: 'success', title: editingEvent ? 'Event updated' : 'Event added', timer: 1300, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please review and try again.', 'error');
    }
  };
  const removeEvent = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Delete event?', text: `${row.title} will be removed.`,
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try { await eduCalendarAPI.deleteEvent(row.id); await loadAll(); Swal.fire({ icon: 'success', title: 'Deleted', timer: 1100, showConfirmButton: false }); }
    catch (err) { Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error'); }
  };

  const TabButton = ({ id, label }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
        tab === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
      }`}
    >
      {label}
    </button>
  );

  const addLabel = tab === 'years' ? 'Add Year' : tab === 'terms' ? 'Add Term' : 'Add Event';
  const onAdd = tab === 'years' ? openYearCreate : tab === 'terms' ? openTermCreate : openEventCreate;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-indigo-600" /> Academic Calendar
          </h2>
          <p className="text-sm text-muted-foreground">
            Academic years, terms and dated events — holidays, exams, admissions and more.
          </p>
        </div>
        {canManage && (
          <Button onClick={onAdd}>
            <Plus className="h-4 w-4 mr-1" /> {addLabel}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Academic Years" value={stats.years} icon={CalendarRange} hint={stats.current ? `Current: ${stats.current}` : undefined} />
        <StatCard label="Terms" value={stats.terms} icon={CalendarCheck} />
        <StatCard label="Events" value={stats.events} icon={CalendarDays} />
        <StatCard label="Teaching-Blocking" value={stats.blocking} icon={Ban} />
      </div>

      <div className="flex items-center gap-2">
        <TabButton id="years" label="Years" />
        <TabButton id="terms" label="Terms" />
        <TabButton id="events" label="Events" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {tab === 'years' ? 'Academic Years' : tab === 'terms' ? 'Terms' : 'Academic Events'}
          </CardTitle>
          <CardDescription>
            {tab === 'years' ? 'Top-level academic year windows for this institute.'
              : tab === 'terms' ? 'Semesters / trimesters within an academic year.'
              : 'Holidays, exams, admissions and other dated events.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading calendar…
            </div>
          ) : tab === 'years' ? (
            years.length === 0 ? (
              <EmptyState icon={CalendarRange} title="No academic years yet"
                description={canManage ? 'Add an academic year to get started.' : 'No academic years have been added yet.'} />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead><TableHead>Starts</TableHead><TableHead>Ends</TableHead>
                      <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {years.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-medium">{row.name}</TableCell>
                        <TableCell>{fmtDate(row.starts_on)}</TableCell>
                        <TableCell>{fmtDate(row.ends_on)}</TableCell>
                        <TableCell>{row.is_current ? <Badge>Current</Badge> : <Badge variant="secondary">—</Badge>}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {canManage && <Button variant="ghost" size="icon" onClick={() => openYearEdit(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                          {canManage && <Button variant="ghost" size="icon" onClick={() => removeYear(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : tab === 'terms' ? (
            terms.length === 0 ? (
              <EmptyState icon={CalendarCheck} title="No terms yet"
                description={canManage ? 'Add a term to an academic year to get started.' : 'No terms have been added yet.'} />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead><TableHead>Academic Year</TableHead><TableHead>Type</TableHead>
                      <TableHead>Starts</TableHead><TableHead>Ends</TableHead><TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {terms.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-medium">{row.name}</TableCell>
                        <TableCell>{yearName(row.academic_year_id)}</TableCell>
                        <TableCell><Badge variant="secondary">{TERM_TYPE_LABEL[row.term_type] || row.term_type || '—'}</Badge></TableCell>
                        <TableCell>{fmtDate(row.starts_on)}</TableCell>
                        <TableCell>{fmtDate(row.ends_on)}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {canManage && <Button variant="ghost" size="icon" onClick={() => openTermEdit(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                          {canManage && <Button variant="ghost" size="icon" onClick={() => removeTerm(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            events.length === 0 ? (
              <EmptyState icon={CalendarDays} title="No events yet"
                description={canManage ? 'Add an academic event (holiday, exam, etc.) to get started.' : 'No events have been added yet.'} />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Term</TableHead>
                      <TableHead>Starts</TableHead><TableHead>Ends</TableHead><TableHead>Blocks Teaching</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-medium">{row.title}</TableCell>
                        <TableCell><Badge variant="secondary">{EVENT_TYPE_LABEL[row.event_type] || row.event_type || '—'}</Badge></TableCell>
                        <TableCell>{termName(row.term_id)}</TableCell>
                        <TableCell>{fmtDate(row.starts_on)}</TableCell>
                        <TableCell>{fmtDate(row.ends_on)}</TableCell>
                        <TableCell>{row.blocks_teaching ? <Badge variant="outline">Yes</Badge> : '—'}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {canManage && <Button variant="ghost" size="icon" onClick={() => openEventEdit(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                          {canManage && <Button variant="ghost" size="icon" onClick={() => removeEvent(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Year dialog */}
      <Dialog open={yearDialog} onOpenChange={setYearDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingYear ? 'Edit Academic Year' : 'Add Academic Year'}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              <div className="space-y-1">
                <Label>Name *</Label>
                <Input value={yearForm.name} onChange={(e) => setYearForm((p) => ({ ...p, name: e.target.value }))} placeholder="2025-2026" />
              </div>
              <div className="space-y-1">
                <Label>Current Year</Label>
                <Select value={yearForm.is_current ? 'yes' : 'no'} onValueChange={(v) => setYearForm((p) => ({ ...p, is_current: v === 'yes' }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Starts On</Label>
                <Input type="date" value={yearForm.starts_on} onChange={(e) => setYearForm((p) => ({ ...p, starts_on: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Ends On</Label>
                <Input type="date" value={yearForm.ends_on} onChange={(e) => setYearForm((p) => ({ ...p, ends_on: e.target.value }))} />
              </div>
            </DialogFormGrid>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setYearDialog(false)}>Cancel</Button>
            <Button onClick={submitYear}>{editingYear ? 'Save changes' : 'Add year'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Term dialog */}
      <Dialog open={termDialog} onOpenChange={setTermDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTerm ? 'Edit Term' : 'Add Term'}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              <div className="space-y-1">
                <Label>Academic Year *</Label>
                <Select value={termForm.academic_year_id} onValueChange={(v) => setTermForm((p) => ({ ...p, academic_year_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select year" /></SelectTrigger>
                  <SelectContent>
                    {years.map((y) => <SelectItem key={y.id} value={String(y.id)}>{y.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Name *</Label>
                <Input value={termForm.name} onChange={(e) => setTermForm((p) => ({ ...p, name: e.target.value }))} placeholder="Autumn Semester" />
              </div>
              <div className="space-y-1">
                <Label>Term Type</Label>
                <Select value={termForm.term_type} onValueChange={(v) => setTermForm((p) => ({ ...p, term_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TERM_TYPES.map((t) => <SelectItem key={t} value={t}>{TERM_TYPE_LABEL[t]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Starts On</Label>
                <Input type="date" value={termForm.starts_on} onChange={(e) => setTermForm((p) => ({ ...p, starts_on: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Ends On</Label>
                <Input type="date" value={termForm.ends_on} onChange={(e) => setTermForm((p) => ({ ...p, ends_on: e.target.value }))} />
              </div>
            </DialogFormGrid>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermDialog(false)}>Cancel</Button>
            <Button onClick={submitTerm}>{editingTerm ? 'Save changes' : 'Add term'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event dialog */}
      <Dialog open={eventDialog} onOpenChange={setEventDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Add Event'}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              <div className="space-y-1">
                <Label>Title *</Label>
                <Input value={eventForm.title} onChange={(e) => setEventForm((p) => ({ ...p, title: e.target.value }))} placeholder="Winter Break" />
              </div>
              <div className="space-y-1">
                <Label>Event Type</Label>
                <Select value={eventForm.event_type} onValueChange={(v) => setEventForm((p) => ({ ...p, event_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{EVENT_TYPE_LABEL[t]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Term</Label>
                <Select value={eventForm.term_id || 'none'} onValueChange={(v) => setEventForm((p) => ({ ...p, term_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {terms.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name} · {yearName(t.academic_year_id)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Blocks Teaching</Label>
                <Select value={eventForm.blocks_teaching ? 'yes' : 'no'} onValueChange={(v) => setEventForm((p) => ({ ...p, blocks_teaching: v === 'yes' }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Starts On</Label>
                <Input type="date" value={eventForm.starts_on} onChange={(e) => setEventForm((p) => ({ ...p, starts_on: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Ends On</Label>
                <Input type="date" value={eventForm.ends_on} onChange={(e) => setEventForm((p) => ({ ...p, ends_on: e.target.value }))} />
              </div>
            </DialogFormGrid>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventDialog(false)}>Cancel</Button>
            <Button onClick={submitEvent}>{editingEvent ? 'Save changes' : 'Add event'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationCalendarManagement;
